// FaceLandmarker lifecycle. Memoized so the model + wasm load once per page;
// never rejects — resolves null on any failure and resets the memo so the
// next attempt retries (e.g. transient offline).
import {
  MODEL_ASSET_PATH,
  WASM_BASE_PATH,
  VISION_BUNDLE_PATH,
  MODEL_LOAD_TIMEOUT_MS,
} from './constants';

let loadPromise = null;

async function createLandmarker() {
  // Runtime import of the vendored ESM bundle (same files as the pinned
  // @mediapipe/tasks-vision npm package — see public/models/README.md).
  // webpackIgnore keeps it out of the webpack graph: bundling it trips a
  // fatal-under-CI "critical dependency" warning, and self-hosting already
  // requires the wasm/model files to live in public/.
  const { FilesetResolver, FaceLandmarker } = await import(
    /* webpackIgnore: true */ VISION_BUNDLE_PATH
  );
  const fileset = await FilesetResolver.forVisionTasks(WASM_BASE_PATH);
  const options = (delegate) => ({
    baseOptions: { modelAssetPath: MODEL_ASSET_PATH, delegate },
    runningMode: 'IMAGE',
    numFaces: 1,
  });
  let landmarker;
  try {
    landmarker = await FaceLandmarker.createFromOptions(
      fileset,
      options('GPU')
    );
  } catch (gpuErr) {
    landmarker = await FaceLandmarker.createFromOptions(
      fileset,
      options('CPU')
    );
  }
  return {
    landmarker,
    tessellation: FaceLandmarker.FACE_LANDMARKS_TESSELATION,
  };
}

// Kick (or join) the load. Resolves { landmarker, tessellation } or null.
export function warmFaceLandmarker() {
  if (!loadPromise) {
    loadPromise = createLandmarker().catch(() => {
      loadPromise = null; // allow a later drop to retry
      return null;
    });
  }
  return loadPromise;
}

// Race the load against a timeout. Timeout resolves null but does NOT cancel
// the underlying load — it keeps warming for the next attempt.
export function getLandmarkerWithTimeout(timeoutMs = MODEL_LOAD_TIMEOUT_MS) {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), timeoutMs);
    warmFaceLandmarker().then((loaded) => {
      clearTimeout(timer);
      resolve(loaded); // no-op if the timeout already resolved
    });
  });
}

// Test-only: reset the memo between cases.
export function _resetForTests() {
  loadPromise = null;
}
